import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, MousePointer, Ruler, RotateCcw, Check, ZoomIn, ZoomOut, Move, AlertCircle, Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { displaySourceFor, loadLotImage, LotImageLoadError, lotImageLoadErrorMessage } from '@/lib/parkingLot/lotImage';

interface CalibrationPoint {
  x: number;
  y: number;
}

interface ParkingLotImage {
  id: string;
  image_path: string;
  image_width: number;
  image_height: number;
  status: string;
  reference_points: CalibrationPoint[];
  scale_ft_per_pixel: number | null;
}

interface ParkingLotCalibrationViewProps {
  image: ParkingLotImage;
  onComplete: () => void;
  onActivate: () => void;
  /** NOV4 P2: offered when the stored image cannot be shown — replaces it with a new upload. */
  onReplaceImage?: () => void;
}

export function ParkingLotCalibrationView({
  image,
  onComplete,
  onActivate,
  onReplaceImage,
}: ParkingLotCalibrationViewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  // What is drawn: the image, or a downscaled copy of an oversized one — always drawn at the natural size so stored
  // calibration points keep their pixel coordinates.
  const [displaySource, setDisplaySource] = useState<CanvasImageSource | null>(null);
  // NOV4 P2: a failed load is an actionable message with Retry, never an endless spinner.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  
  // Calibration state
  const [pointA, setPointA] = useState<CalibrationPoint | null>(null);
  const [pointB, setPointB] = useState<CalibrationPoint | null>(null);
  const [distanceFt, setDistanceFt] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Pan/zoom state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
  
  const { toast } = useToast();

  // Sign the stored path, then load the image — every failure ends in `loadError`.
  useEffect(() => {
    let cancelled = false;
    setImageLoaded(false);
    setLoadError(null);
    (async () => {
      try {
        const { data, error } = await supabase.storage
          .from('parking-lot-images')
          .createSignedUrl(image.image_path, 3600); // 1 hour expiry
        if (error || !data?.signedUrl) throw new LotImageLoadError('signed_url', error?.message);
        const img = await loadLotImage(data.signedUrl);
        if (cancelled) return;
        setImageElement(img);
        setDisplaySource(displaySourceFor(img));
        setImageLoaded(true);

        // Calculate initial scale to fit container
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const containerHeight = containerRef.current.clientHeight;
          const imgAspect = img.naturalWidth / img.naturalHeight;
          const containerAspect = containerWidth / containerHeight;

          let initialScale: number;
          if (imgAspect > containerAspect) {
            initialScale = containerWidth / img.naturalWidth;
          } else {
            initialScale = containerHeight / img.naturalHeight;
          }
          setScale(initialScale * 0.9); // 90% to leave some padding
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load parking lot image:', err);
        setLoadError(err instanceof LotImageLoadError ? err.message : lotImageLoadErrorMessage('decode'));
      }
    })();
    return () => { cancelled = true; };
  }, [image.image_path, loadAttempt]);

  // Draw canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !imageElement || !displaySource) return;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw image with transform
    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);
    ctx.drawImage(displaySource, 0, 0, imageElement.naturalWidth, imageElement.naturalHeight);
    ctx.restore();

    // Draw calibration points and line
    const drawPoint = (point: CalibrationPoint, label: string, color: string) => {
      const screenX = point.x * scale + offset.x;
      const screenY = point.y * scale + offset.y;
      
      ctx.beginPath();
      ctx.arc(screenX, screenY, 8, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      ctx.font = 'bold 14px sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.strokeText(label, screenX + 12, screenY + 5);
      ctx.fillText(label, screenX + 12, screenY + 5);
    };

    if (pointA) {
      drawPoint(pointA, 'A', '#3b82f6');
    }
    
    if (pointB) {
      drawPoint(pointB, 'B', '#10b981');
    }
    
    // Draw line between points
    if (pointA && pointB) {
      const ax = pointA.x * scale + offset.x;
      const ay = pointA.y * scale + offset.y;
      const bx = pointB.x * scale + offset.x;
      const by = pointB.y * scale + offset.y;
      
      ctx.beginPath();
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Draw pixel distance label
      const pixelDist = Math.sqrt(
        Math.pow(pointB.x - pointA.x, 2) + Math.pow(pointB.y - pointA.y, 2)
      );
      const midX = (ax + bx) / 2;
      const midY = (ay + by) / 2;
      
      ctx.font = '12px sans-serif';
      ctx.fillStyle = '#fbbf24';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeText(`${Math.round(pixelDist)}px`, midX, midY - 10);
      ctx.fillText(`${Math.round(pixelDist)}px`, midX, midY - 10);
    }
  }, [imageElement, displaySource, scale, offset, pointA, pointB]);

  // Redraw on changes
  useEffect(() => {
    draw();
  }, [draw]);

  // Handle canvas resize
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
      draw();
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [draw]);

  // Handle canvas clicks
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    
    // Convert screen coords to image coords
    const imageX = (screenX - offset.x) / scale;
    const imageY = (screenY - offset.y) / scale;
    
    // Validate within image bounds
    if (imageX < 0 || imageX > image.image_width || imageY < 0 || imageY > image.image_height) {
      return;
    }
    
    if (!pointA) {
      setPointA({ x: imageX, y: imageY });
    } else if (!pointB) {
      setPointB({ x: imageX, y: imageY });
    }
  };

  // Pan/zoom handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 1 || e.ctrlKey) { // Middle mouse or ctrl+click
      setIsPanning(true);
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      const dx = e.clientX - lastPanPoint.x;
      const dy = e.clientY - lastPanPoint.y;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(5, scale * delta));
    
    // Zoom towards mouse position
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const newOffsetX = mouseX - (mouseX - offset.x) * (newScale / scale);
      const newOffsetY = mouseY - (mouseY - offset.y) * (newScale / scale);
      
      setOffset({ x: newOffsetX, y: newOffsetY });
    }
    
    setScale(newScale);
  };

  // Calculate scale and save
  const handleSaveCalibration = async () => {
    if (!pointA || !pointB || !distanceFt) return;
    
    const distance = parseFloat(distanceFt);
    if (isNaN(distance) || distance <= 0) {
      toast({
        title: 'Invalid distance',
        description: 'Please enter a valid distance in feet.',
        variant: 'destructive',
      });
      return;
    }
    
    const pixelDist = Math.sqrt(
      Math.pow(pointB.x - pointA.x, 2) + Math.pow(pointB.y - pointA.y, 2)
    );
    const scaleFtPerPixel = distance / pixelDist;
    
    setIsSaving(true);
    
    try {
      const { error } = await supabase.rpc('save_parking_lot_calibration', {
        p_image_id: image.id,
        p_reference_points: JSON.stringify([pointA, pointB]),
        p_scale_ft_per_pixel: scaleFtPerPixel,
        p_rotation_deg: 0,
      });
      
      if (error) throw error;
      
      toast({
        title: 'Calibration saved',
        description: `Scale: ${scaleFtPerPixel.toFixed(4)} ft/pixel`,
      });
      
      onComplete();
    } catch (err: any) {
      console.error('Calibration error:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to save calibration',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPointA(null);
    setPointB(null);
    setDistanceFt('');
  };

  const pixelDistance = pointA && pointB
    ? Math.sqrt(Math.pow(pointB.x - pointA.x, 2) + Math.pow(pointB.y - pointA.y, 2))
    : null;

  const calculatedScale = pixelDistance && distanceFt && parseFloat(distanceFt) > 0
    ? parseFloat(distanceFt) / pixelDistance
    : null;

  return (
    <div className="flex gap-4 h-[600px]">
      {/* Canvas area */}
      <div 
        ref={containerRef}
        className="flex-1 relative bg-muted rounded-lg overflow-hidden"
      >
        {loadError ? (
          <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="parking-lot-image-error">
            <div className="max-w-md text-center space-y-4">
              <AlertCircle className="h-8 w-8 mx-auto text-destructive" />
              <p className="text-sm">{loadError}</p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => setLoadAttempt((n) => n + 1)} data-testid="parking-lot-image-retry">
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Try again
                </Button>
                {onReplaceImage && (
                  <Button onClick={onReplaceImage} data-testid="parking-lot-image-replace">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload a different image
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : !imageLoaded ? (
          <div className="absolute inset-0 flex items-center justify-center" data-testid="parking-lot-image-loading">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            className={cn(
              'w-full h-full',
              isPanning ? 'cursor-grabbing' : pointA && pointB ? 'cursor-default' : 'cursor-crosshair'
            )}
            onClick={handleCanvasClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          />
        )}
        
        {/* Zoom controls */}
        <div className="absolute bottom-4 right-4 flex flex-col gap-1">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => setScale(s => Math.min(5, s * 1.2))}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => setScale(s => Math.max(0.1, s / 1.2))}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Instructions overlay */}
        {imageLoaded && !pointA && (
          <div className="absolute top-4 left-4 right-4">
            <Card className="bg-background/90 backdrop-blur">
              <CardContent className="p-3 flex items-center gap-3">
                <MousePointer className="h-5 w-5 text-primary" />
                <span className="text-sm">
                  <strong>Step 1:</strong> Click to place Point A on a known reference
                </span>
              </CardContent>
            </Card>
          </div>
        )}
        
        {imageLoaded && pointA && !pointB && (
          <div className="absolute top-4 left-4 right-4">
            <Card className="bg-background/90 backdrop-blur">
              <CardContent className="p-3 flex items-center gap-3">
                <MousePointer className="h-5 w-5 text-green-500" />
                <span className="text-sm">
                  <strong>Step 2:</strong> Click to place Point B - choose a distance you can measure in feet
                </span>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Calibration controls */}
      <Card className="w-80 flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ruler className="h-5 w-5" />
            Calibration
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col gap-4">
          {/* Status badges */}
          <div className="flex gap-2">
            <Badge variant={pointA ? 'default' : 'outline'}>
              Point A {pointA && '✓'}
            </Badge>
            <Badge variant={pointB ? 'default' : 'outline'}>
              Point B {pointB && '✓'}
            </Badge>
          </div>
          
          {/* Distance input */}
          {pointA && pointB && (
            <div className="space-y-2">
              <Label htmlFor="distance">Real-world distance (feet)</Label>
              <Input
                id="distance"
                type="number"
                min="1"
                step="0.1"
                placeholder="e.g., 50"
                value={distanceFt}
                onChange={(e) => setDistanceFt(e.target.value)}
              />
              
              {pixelDistance && (
                <p className="text-xs text-muted-foreground">
                  Pixel distance: {Math.round(pixelDistance)}px
                </p>
              )}
              
              {calculatedScale && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Calculated Scale</p>
                  <p className="text-lg font-bold text-primary">
                    {calculatedScale.toFixed(4)} ft/pixel
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    ≈ {(1 / calculatedScale).toFixed(1)} pixels per foot
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Navigation help */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p className="flex items-center gap-2">
              <Move className="h-3 w-3" />
              Ctrl+click or middle mouse to pan
            </p>
            <p className="flex items-center gap-2">
              <ZoomIn className="h-3 w-3" />
              Scroll to zoom
            </p>
          </div>
          
          <div className="mt-auto space-y-2">
            {(pointA || pointB) && (
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={handleReset}
              >
                <RotateCcw className="h-4 w-4" />
                Reset Points
              </Button>
            )}
            
            {image.status === 'CALIBRATED' && (
              <Button
                className="w-full gap-2"
                onClick={onActivate}
              >
                <Check className="h-4 w-4" />
                Activate & Continue to Layout
              </Button>
            )}
            
            <Button
              className="w-full gap-2"
              disabled={!pointA || !pointB || !distanceFt || isSaving}
              onClick={handleSaveCalibration}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Save Calibration
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
